<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';
import type { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { checkGroupDeletePermission } from '@ilteducation/customer-mgmt-permissions';

import { useLocale } from '../../composables/useLocale';
import useModal, { ModalTypes, modals } from '../../composables/useModal';
import { usePermissions } from '../../composables/usePermissions';
import EditGroup from './EditGroup.vue';
import Modal from '../Modal.vue';

const { t } = useLocale();

export type Props = {
  name: ModalTypes;
  group: CustomerMgmtTypes.GetGroupResponse;
};
const props = defineProps<Props>();
const emits = defineEmits<{
  (event: 'groupUpdated', value: string): void;
}>();

const { smAndDown } = useDisplay();
const { isOpen, open, close } = useModal(props.name);
const { open: openDeleteGroupModal } = useModal(modals.DELETE_GROUP);
const { permissionContext, reporterBoolean, getCallerAssociation, getGroupMemberships } = usePermissions();

const formRef = ref();
const isUpdating = ref(false);

const isGroupDeletable = ref<ReturnType<typeof reporterBoolean>>(false);
watch(
  [props.group, isOpen],
  () => {
    if (!isOpen.value) {
      return;
    }
    checkGroupDeletePermission(
      permissionContext.value,
      {
        ...props.group,
        organization: props.group.schoolUnit,
      },
      getCallerAssociation,
      getGroupMemberships,
      reporterBoolean,
    ).then((res) => {
      isGroupDeletable.value = res as ReturnType<typeof reporterBoolean>;
    });
  },
  { immediate: true },
);

const doUpdate = async () => {
  isUpdating.value = true;
  if (await formRef.value.updateGroup()) {
    emits('groupUpdated', props.group.uid);
    close();
  }

  isUpdating.value = false;
};

const onDeleteGroup = () => {
  openDeleteGroupModal({ onBack: open });
  close();
};

const canUpdateGroup = computed(() => {
  return formRef?.value?.isGroupInfoChanged && formRef?.value?.valid === true;
});

const reset = () => {
  isUpdating.value = false;
  formRef.value.reset();
};
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.edit_group')"
    :is-open="isOpen"
    :top-left-btn-action="smAndDown ? close : undefined"
    :show-close-btn="!smAndDown"
    top-left-btn-icon="mdi-chevron-left"
    width="560"
    :action="doUpdate"
    :action-disabled="!canUpdateGroup || isUpdating"
    :action-label="t('lcp.save')"
    :loading="isUpdating"
    :secondary-action="onDeleteGroup"
    :secondary-action-label="t('lcp.delete_group')"
    :secondary-action-disabled="!isGroupDeletable"
    secondary-action-color="error"
    :cancel-action="close"
    :cancel-action-label="t('lcp.cancel')"
    @close="reset"
  >
    <EditGroup ref="formRef" :group="group" />
  </Modal>
</template>
